import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setTopNavbar } from '../../Reducers/actions/navbarActions';
import { createShippingMethod, updateShippingMethod } from '../../Reducers/actions/shippingMethodsActions';
import Icon from '../Icon/Icon';
import DestinationSelector from '../DestinationSelector/DestinationSelector';
import './AddEditShippingMethod.scss';

// Convert backend destinations to frontend format (moved outside component to avoid hook issues)
const convertBackendDestinationsToFrontend = (backendDestinations) => {
    if (!backendDestinations || !Array.isArray(backendDestinations)) {
        return [];
    }

    // Backend returns destinations as: { country_code: "FR", country_name: "France" }
    // We need to convert them back to frontend format for editing
    // Since we only store countries in DB, we'll just use the country names
    return backendDestinations.map(dest => {
        // If it's already in frontend format (from DestinationSelector), return as-is
        if (dest.type || dest.destination_type) {
            return dest;
        }

        // If it's from backend (has country_code or country_name)
        if (dest.country_code || dest.country_name) {
            return {
                id: dest.country_code?.toLowerCase() || dest.country_name?.toLowerCase().replace(/\s+/g, '-'),
                name: dest.country_name || dest.name,
                type: 'country'
            };
        }

        // Fallback
        return dest;
    });
};

// Format destinations for backend API (moved outside component to avoid hook issues)
const formatDestinationsForBackend = (destinations) => {
    return destinations.map(dest => {
        const type = dest.type || dest.destination_type;
        const id = dest.id || dest.destination_id;
        const name = dest.name || dest.destination_name;

        // For regions and sub-regions: send as simple string
        if (type === 'region' || type === 'sub-region') {
            return id; // e.g., "europe", "northern-europe"
        }

        // For countries: send as object with name property
        // Backend will handle country name to ISO code conversion
        if (type === 'country') {
            return { name: name }; // e.g., { name: "France" }
        }

        // Fallback: if we don't know the type, try to infer
        // If it looks like a region/sub-region ID (kebab-case), send as string
        if (id && id.includes('-') && !id.match(/^[A-Z]{2}$/)) {
            return id;
        }

        // Otherwise, send as object with name
        return { name: name || id };
    });
};

const AddEditShippingMethod = ({ method, onSave, onCancel }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(method?.type || 'general');
    const [showDestinationSelector, setShowDestinationSelector] = useState(false);
    const [formData, setFormData] = useState({
        type: method?.type || 'general',
        title: method?.title || '',
        destinations: method?.destinations || [],
        rates: method?.rates || [{ name: '', description: '', rate: '', additional_item_rate: '' }]
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        dispatch(setTopNavbar({
            title: method ? 'Edit shipping method' : 'New shipping method',
            showBackArrow: true
        }));

        return () => {
            // Cleanup: restore previous navbar state when component unmounts
            dispatch(setTopNavbar({
                title: 'SHIPPING METHODS',
                showBackArrow: true
            }));
        };
    }, [method, dispatch]);

    useEffect(() => {
        if (method) {
            setFormData({
                type: method.type || 'general',
                title: method.title || '',
                destinations: convertBackendDestinationsToFrontend(method.destinations),
                rates: method.rates && method.rates.length > 0 
                    ? method.rates.map(rate => ({
                        name: rate.name || '',
                        description: rate.description || '',
                        rate: rate.rate != null ? String(rate.rate) : '',
                        additional_item_rate: rate.additional_item_rate != null ? String(rate.additional_item_rate) : ''
                    }))
                    : [{ name: '', description: '', rate: '', additional_item_rate: '' }]
            });
            setActiveTab(method.type || 'general');
        } else {
            // Reset form for new method
            setFormData({
                type: 'general',
                title: '',
                destinations: [],
                rates: [{ name: '', description: '', rate: '', additional_item_rate: '' }]
            });
            setActiveTab('general');
        }
    }, [method]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setFormData(prev => ({
            ...prev,
            type: tab,
            title: tab === 'specific' ? prev.title : ''
        }));
        setFormErrors({});
    };

    const handleDestinationSelect = (selectedDestinations) => {
        setFormData(prev => ({
            ...prev,
            destinations: selectedDestinations
        }));
        setShowDestinationSelector(false);
        // Clear destination error
        if (formErrors.destinations) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.destinations;
                return newErrors;
            });
        }
    };

    const handleRateChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            rates: prev.rates.map((rate, i) => 
                i === index ? { ...rate, [field]: value } : rate
            )
        }));
        // Clear error for this rate field
        if (formErrors[`rate_${index}_${field}`]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`rate_${index}_${field}`];
                return newErrors;
            });
        }
    };

    const handleAddRate = () => {
        setFormData(prev => ({
            ...prev,
            rates: [...prev.rates, { name: '', description: '', rate: '', additional_item_rate: '' }]
        }));
    };

    const handleRemoveRate = (index) => {
        if (formData.rates.length > 1) {
            setFormData(prev => ({
                ...prev,
                rates: prev.rates.filter((_, i) => i !== index)
            }));
        }
    };

    const handleTitleChange = (value) => {
        setFormData(prev => ({
            ...prev,
            title: value
        }));
        if (formErrors.title) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.title;
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const errors = {};

        // Validate destinations
        if (!formData.destinations || formData.destinations.length === 0) {
            errors.destinations = 'At least one destination is required';
        }

        // Validate title for specific type
        if (activeTab === 'specific' && !formData.title.trim()) {
            errors.title = 'Title is required';
        }

        // Validate rates
        formData.rates.forEach((rate, index) => {
            if (!rate.name.trim()) {
                errors[`rate_${index}_name`] = 'Rate name is required';
            }
            if (!rate.description.trim()) {
                errors[`rate_${index}_description`] = 'Description is required';
            }
            if (!rate.rate || parseFloat(rate.rate) <= 0) {
                errors[`rate_${index}_rate`] = 'Valid rate is required';
            }
        });

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const formattedDestinations = formatDestinationsForBackend(formData.destinations);
            
            const payload = {
                type: activeTab,
                title: activeTab === 'specific' ? formData.title.trim() : null,
                destinations: formattedDestinations,
                rates: formData.rates.map((rate, index) => ({
                    name: rate.name.trim(),
                    description: rate.description.trim(),
                    rate: parseFloat(rate.rate),
                    additional_item_rate: rate.additional_item_rate && rate.additional_item_rate.trim() 
                        ? parseFloat(rate.additional_item_rate) 
                        : null,
                    sort_order: index
                }))
            };

            // Debug: log payload to see what we're sending
            console.log('Shipping method payload:', JSON.stringify(payload, null, 2));

            let result;
            if (method) {
                result = await dispatch(updateShippingMethod(method.shipping_method_id || method.id, payload));
            } else {
                result = await dispatch(createShippingMethod(payload));
            }

            if (result?.success) {
                setIsSubmitting(false);
                if (onSave) onSave();
            } else {
                setIsSubmitting(false);
                // Handle error - you might want to show an error message
                console.error('Error saving shipping method:', result?.response);
            }
        } catch (error) {
            console.error('Error saving shipping method:', error);
            setIsSubmitting(false);
        }
    };

    const formatDestinations = () => {
        if (!formData.destinations || formData.destinations.length === 0) {
            return 'Select destination';
        }
        if (formData.destinations.length === 1) {
            return formData.destinations[0].name || formData.destinations[0];
        }
        return `${formData.destinations.length} destinations selected`;
    };

    return (
        <div className="add-edit-shipping-method">
            {/* Tabs */}
            <div className="d-flex aic jcc area-container w-full">
                <span 
                    className={` area price fs-real fw-bold ${activeTab === 'general' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`} 
                    onClick={() => handleTabChange('general')}
                >
                    General
                </span>
                <span 
                    className={`ml-8 area price fs-real fw-bold ${activeTab === 'specific' ? 'bg-dark fg-white' : 'bg-white fg-dark'}`} 
                    onClick={() => handleTabChange('specific')}
                >
                    Specific
                </span>
            </div>

            <form className="shipping-method-form mt-16" onSubmit={handleSubmit}>
                {/* Title field for Specific type */}
                {activeTab === 'specific' && (
                    <div className="form-field">
                        <label className="fs-xs fw-bold fg-dark mb-8">
                            Title (Paintings, Postal cards...etc)
                        </label>
                        <input
                            type="text"
                            className={`input-main w-full ${formErrors.title ? 'input-error' : ''}`}
                            placeholder="Enter title"
                            value={formData.title || ''}
                            onChange={(e) => handleTitleChange(e.target.value)}
                        />
                        {formErrors.title && (
                            <p className="form-error">{formErrors.title}</p>
                        )}
                    </div>
                )}

                {/* Destination selector */}
                <div className="form-field">
                    <label className="fs-xs fw-bold fg-dark mb-8">Destination</label>
                    <div
                        className={`destination-selector-input ${formErrors.destinations ? 'input-error' : ''} c-pointer`}
                        onClick={() => setShowDestinationSelector(true)}
                    >
                        <span className={formData.destinations.length === 0 ? 'placeholder' : ''}>
                            {formatDestinations()}
                        </span>
                        <Icon alias="chevron-down" width={16} color="#1C1B1F" />
                    </div>
                    {formErrors.destinations && (
                        <p className="form-error">{formErrors.destinations}</p>
                    )}
                </div>

                {/* Rates */}
                <div className="rates-section">
                    {formData.rates.map((rate, index) => (
                        <div key={index} >
                            <div className="rate-header">
                                <p className="fs-xs fw-bold fg-dark">Rate {index + 1}</p>
                                {formData.rates.length > 1 && (
                                    <button
                                        type="button"
                                        className="remove-rate-btn"
                                        onClick={() => handleRemoveRate(index)}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>

                            <div className="form-field">
                                <input
                                    type="text"
                                    className={`input-main w-full ${formErrors[`rate_${index}_name`] ? 'input-error' : ''}`}
                                    placeholder="Name (standard, express..etc)"
                                    value={rate.name || ''}
                                    onChange={(e) => handleRateChange(index, 'name', e.target.value)}
                                />
                                {formErrors[`rate_${index}_name`] && (
                                    <p className="form-error">{formErrors[`rate_${index}_name`]}</p>
                                )}
                            </div>

                            <div className="form-field">
                                <input
                                    type="text"
                                    className={`input-main w-full ${formErrors[`rate_${index}_description`] ? 'input-error' : ''}`}
                                    placeholder="Description (5 to 7 days)"
                                    value={rate.description || ''}
                                    onChange={(e) => handleRateChange(index, 'description', e.target.value)}
                                />
                                {formErrors[`rate_${index}_description`] && (
                                    <p className="form-error">{formErrors[`rate_${index}_description`]}</p>
                                )}
                            </div>

                            <div className="form-field">
                                <div className="input-with-currency">
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className={`input-main w-full ${formErrors[`rate_${index}_rate`] ? 'input-error' : ''}`}
                                        placeholder="Rate"
                                        value={rate.rate || ''}
                                        onChange={(e) => handleRateChange(index, 'rate', e.target.value)}
                                    />
                                    <span className="currency-symbol">€</span>
                                </div>
                                {formErrors[`rate_${index}_rate`] && (
                                    <p className="form-error">{formErrors[`rate_${index}_rate`]}</p>
                                )}
                            </div>

                            <div className="form-field">
                                <div className="input-with-currency">
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="input-main w-full "
                                        placeholder="Rate for additional items (optional)"
                                        value={rate.additional_item_rate || ''}
                                        onChange={(e) => handleRateChange(index, 'additional_item_rate', e.target.value)}
                                    />
                                    <span className="currency-symbol">€</span>
                                </div>
                            </div>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="add-rate-btn"
                        onClick={handleAddRate}
                    >
                        ADD MORE RATES +
                    </button>
                </div>

                {/* Submit buttons */}
                <div className="form-actions">
                    <button
                        type="submit"
                        className="btn red-btn btn-full"
                        disabled={isSubmitting}
                        style={{ height: '64px', marginTop: '24px' }}
                    >
                        {isSubmitting ? 'SAVING...' : 'SAVE SHIPPING METHOD'}
                    </button>
                    <p
                        className="fs-xxs fg-dark fw-bold mb-16 mt-16 ta-center c-pointer td-underline"
                        onClick={onCancel}
                    >
                        CANCEL
                    </p>
                </div>
            </form>

            {/* Destination Selector Modal */}
            {showDestinationSelector && (
                <DestinationSelector
                    key="destination-selector"
                    selectedDestinations={formData.destinations}
                    onSelect={handleDestinationSelect}
                    onClose={() => setShowDestinationSelector(false)}
                />
            )}
        </div>
    );
};

export default AddEditShippingMethod;

